import React from 'react';
import { Sprite, Text, Container } from '@inlet/react-pixi';
import { TextStyle } from '@pixi/text';
import { InteractionEvent, Texture } from 'pixi.js';

import textBoardSvg from '../../../assets/island-sign/middle-board.svg';
import leftPostSvg from '../../../assets/island-sign/left-post.svg';
import rightPostSvg from '../../../assets/island-sign/right-post.svg';
import leftBoardSvg from '../../../assets/island-sign/left-board.svg';
import rightBoardSvg from '../../../assets/island-sign/right-board.svg';
import { useNonDragClick } from '../../../hooks/useNonDragClick';

interface Props {
  islandName: string;
  worldWidth: number;
  worldHeight: number;
  onSaveIsland: () => void;
}
/**
 * If this value changes, most of the other values on the page have to as well.
 */
const TEXT_BASELINE_CHARACTER_COUNT = 10;

const IslandSign = ({
  islandName, worldWidth, worldHeight, onSaveIsland,
}: Props): React.ReactElement => {
  const [onPointerDown, onPointerUp] = useNonDragClick();
  /**
   * The position of the sign.
   */
  const xSignOffset = (worldWidth * 10) / 100;
  const ySignOffset = (worldHeight * 65) / 100;

  const onSaveIslandClick = (event: InteractionEvent) => {
    event.stopPropagation();
    onSaveIsland();
  };

  const textBoardWidth = (worldWidth * 12) / 100;
  const textBoardHeight = (worldWidth * 3) / 100;
  const textBoard = Texture.from(textBoardSvg);

  const islandNameLength = islandName.length;

  /**
   * The text width and hight have to be absolute values due to the letters stretching to cover all
   * the available space.
   *
   * The boards fits 10 characters perfectly which means a character is 10% of that.
   * However, the board size is capped at 10 characters.
   */
  const textWidth = islandNameLength > TEXT_BASELINE_CHARACTER_COUNT
    ? textBoardWidth
    : textBoardWidth * (0.1 * islandNameLength);
  /**
   * The height of the board stays the same for island names up to and including 10 characters.
   * Once we go over that limit, we start reducing the height of the text element to 93% for every
   * character over the 10 character limit.
   */
  const textHeight = islandNameLength <= TEXT_BASELINE_CHARACTER_COUNT
    ? textBoardHeight
    : textBoardHeight * (0.93 ** (islandNameLength - TEXT_BASELINE_CHARACTER_COUNT));

  const leftPost = Texture.from(leftPostSvg);
  const rightPost = Texture.from(rightPostSvg);

  const postHeight = textBoardHeight * 2.7;
  /**
   * The post images are 31x91 pixels wide and so we set the post width accordingly.
   */
  const postWidth = postHeight / 3;

  const leftBoard = Texture.from(leftBoardSvg);
  const rightBoard = Texture.from(rightBoardSvg);
  const sideBoardWidth = textBoardHeight / 3;

  const sideBoardOffsetX = (textBoardWidth / 2);
  const sideBoardOffsetY = (textBoardHeight / 2);

  return (
    <Container>
      <Sprite
        texture={leftPost}
        x={xSignOffset - sideBoardOffsetX}
        y={ySignOffset + (postHeight / 4)}
        height={postHeight}
        width={postWidth}
        anchor={0.5}
      />

      <Sprite
        texture={rightPost}
        x={xSignOffset + sideBoardOffsetX}
        y={ySignOffset + (postHeight / 4)}
        height={postHeight}
        width={postWidth}
        anchor={0.5}
      />

      <Sprite
        texture={leftBoard}
        x={xSignOffset - sideBoardOffsetX}
        y={ySignOffset + sideBoardOffsetY}
        height={textBoardHeight}
        width={sideBoardWidth}
        anchor={1}
      />

      <Sprite
        texture={rightBoard}
        x={xSignOffset + sideBoardOffsetX}
        y={ySignOffset - sideBoardOffsetY}
        height={textBoardHeight}
        width={sideBoardWidth}
        anchor={0}
      />

      <Sprite
        texture={textBoard}
        x={xSignOffset}
        y={ySignOffset}
        width={textBoardWidth}
        height={textBoardHeight}
        anchor={0.5}
        pointerdown={onPointerDown}
        pointerup={(event) => onPointerUp(event, () => onSaveIslandClick(event))}
      />

      {/* The 0.07 pushes the text slightly further down to centre it in the middle of the board */}
      <Text
        anchor={0.5}
        text={islandName}
        x={xSignOffset}
        y={ySignOffset + (textBoardHeight * 0.07)}
        pointerdown={onPointerDown}
        pointerup={(event) => onPointerUp(event, () => onSaveIslandClick(event))}
        interactive
        cursor="pointer"
        height={textHeight}
        width={textWidth}
        style={
          new TextStyle({
            align: 'center',
            fontFamily: 'Gorrila',
            fill: '#FFE472',
            dropShadowColor: '#AD6B11',
            dropShadow: true,
            dropShadowDistance: 3,
            dropShadowAngle: 45,
            fontSize: 50,
          })
        }
      />
    </Container>
  );
};

export default IslandSign;
